import type { DreMouseEvent } from './types';
import { useState, useCallback, useContext, useEffect, useRef } from 'react';
import useLatest from 'react-use/esm/useLatest';
import { useSetRecoilState } from 'recoil';
import { Contexts } from 'react-vtk-js';
import type { MouseHandlers } from '../types';
import { useMouseToWorldPosition } from './useMouseWorldPosition';
import { useEventAction } from './usePicker';
import { useViewportId, mouseAdaptorsState } from './state';

type Props = {
  onDragStart?: Nullable<MouseHandlers['onDragStart']>;
  onDrag?: Nullable<MouseHandlers['onDrag']>;
  onDragEnd?: Nullable<MouseHandlers['onDragEnd']>;
};

// Keeps a pointer position on the canvas, so letting go far outside it can't place an annotation far
// off screen. It stays a pixel in from every edge, because exactly (0, 0) reads as "no position yet"
// to the world position conversion.
const EDGE_MARGIN = 1;
const clampToCanvas = (position: number, size: number): number =>
  Math.min(Math.max(position, EDGE_MARGIN), size - EDGE_MARGIN);

type EventHandlers = {
  onMouseDown: NonNullable<MouseHandlers['onMouseDown']>;
  onMouseUp: NonNullable<MouseHandlers['onMouseUp']>;
};

export const useDragEvents = ({ onDrag, onDragStart, onDragEnd }: Props): EventHandlers => {
  const viewportId = useViewportId();
  const setMouseAdaptorsState = useSetRecoilState(mouseAdaptorsState(viewportId));
  const [primitiveInfo, setPrimitiveInfo] = useState(null);
  const mouseToWorldPosition = useMouseToWorldPosition();
  const view = useContext(Contexts.ViewContext);

  const [dragging, setDragging] = useState(false);
  // The drag also lives in refs, so the window listener below sees the current drag, not a stale one.
  const draggingRef = useRef(false);
  const stopListeningForReleaseRef = useRef<(() => void) | null>(null);

  const getMouseWorldPosition = useCallback(
    async (
      displayPosition?: {
        x: number;
        y: number;
      } | null
    ) => {
      const canvas = view?.getOpenGLRenderWindow()?.get().getCanvas();
      if (displayPosition == null || canvas == null) return null;

      const { left, top, width, height } = canvas.getBoundingClientRect();
      const posX = left + window.pageXOffset;
      const posY = top + window.pageYOffset;
      const elX = clampToCanvas(displayPosition.x - posX, width);
      const elY = clampToCanvas(displayPosition.y - posY, height);

      return mouseToWorldPosition({ elX, elY });
    },
    [mouseToWorldPosition, view]
  );

  useEventAction('mouseMove', async ({ displayPosition }) => {
    if (dragging == null || primitiveInfo == null || displayPosition == null) {
      return;
    }

    // @ts-expect-error [EN-10263] - TS2698 - Spread types may only be created from object types.
    onDrag?.({ ...primitiveInfo, worldPosition: await getMouseWorldPosition(displayPosition) });
  });

  const endDrag = useCallback(
    async (info: DreMouseEvent) => {
      setMouseAdaptorsState((state) => ({ ...state, disabled: false }));
      stopListeningForReleaseRef.current?.();
      if (!draggingRef.current) return;

      draggingRef.current = false;
      setDragging(false);
      setPrimitiveInfo(null);
      onDragEnd?.({ ...info, worldPosition: await getMouseWorldPosition(info.displayPosition) });
    },
    [onDragEnd, setMouseAdaptorsState, getMouseWorldPosition]
  );
  const latestEndDrag = useLatest(endDrag);

  // Letting go of the mouse outside the viewport never reaches the viewport's own handlers, which
  // would leave the drag going (and the mouse tools disabled) until the next click. So a drag also
  // listens for the release on the window.
  useEffect(() => () => stopListeningForReleaseRef.current?.(), []);

  const onMouseDown = useCallback(
    async (info: DreMouseEvent) => {
      setMouseAdaptorsState((state) => ({ ...state, disabled: true }));

      draggingRef.current = true;
      setDragging(true);
      // @ts-expect-error [EN-10263] - TS2345 - Argument of type 'DreMouseEvent' is not assignable to parameter of type 'SetStateAction<null>'.
      setPrimitiveInfo(info);

      stopListeningForReleaseRef.current?.();
      const onWindowMouseUp = (event: MouseEvent) => {
        latestEndDrag.current({
          ...info,
          displayPosition: {
            x: event.clientX + window.pageXOffset,
            y: event.clientY + window.pageYOffset,
          },
        });
      };
      window.addEventListener('mouseup', onWindowMouseUp);
      stopListeningForReleaseRef.current = () => {
        window.removeEventListener('mouseup', onWindowMouseUp);
        stopListeningForReleaseRef.current = null;
      };

      onDragStart?.({ ...info, worldPosition: await getMouseWorldPosition(info.displayPosition) });
    },
    [onDragStart, setMouseAdaptorsState, getMouseWorldPosition, latestEndDrag]
  );

  const onMouseUp = endDrag;

  return {
    onMouseDown,
    onMouseUp,
  };
};
